/**
 * Facility Types Configuration
 * Defines facility types (Aged Care) and their associated UI features
 */

import FacilityService from '../services/api/FacilityService';

export const FACILITY_TYPES = {
  AGED_CARE: 'agedcare',
  IN_HOUSE: 'inhouse'
};

/**
 * Facility Configuration
 * Maps facility IDs to their types and available features
 */
export const FACILITY_CONFIG = {
  // Aged Care Facilities
  'sunshine': {
    id: 'sunshine',
    name: 'Sunshine Gardens Aged Care',
    type: FACILITY_TYPES.AGED_CARE,
    types: [FACILITY_TYPES.AGED_CARE],
    systemContext: 'agedcare',
    features: {
      anacc: true,
      enrmc: true,
      supportAtHome: true
    }
  },
  'bayview': {
    id: 'bayview',
    name: 'Bayview Gardens Aged Care',
    type: FACILITY_TYPES.AGED_CARE,
    types: [FACILITY_TYPES.AGED_CARE],
    systemContext: 'agedcare',
    features: {
      anacc: true,
      enrmc: true,
      supportAtHome: true
    }
  },
  'greenhill': {
    id: 'greenhill',
    name: 'Green Hill Residence',
    type: FACILITY_TYPES.AGED_CARE,
    types: [FACILITY_TYPES.AGED_CARE],
    systemContext: 'agedcare',
    features: {
      anacc: true,
      enrmc: true,
      supportAtHome: true
    }
  },
  'rosewood': {
    id: 'rosewood',
    name: 'Rosewood Care Community',
    type: FACILITY_TYPES.AGED_CARE,
    types: [FACILITY_TYPES.AGED_CARE],
    systemContext: 'agedcare',
    features: {
      anacc: true,
      enrmc: true,
      supportAtHome: true
    }
  },
  // In-House / Home Care Facilities
  'community': {
    id: 'community',
    name: 'Community Care Services',
    type: FACILITY_TYPES.IN_HOUSE,
    types: [FACILITY_TYPES.IN_HOUSE],
    systemContext: 'agedcare',
    features: {
      anacc: true,
      enrmc: true,
      supportAtHome: true
    }
  }
};

/**
 * Get facility configuration by ID (synchronous)
 * @param {string} facilityId - Facility ID
 * @returns {object} Facility configuration
 */
export const getFacilityConfig = (facilityId) => {
  if (FACILITY_CONFIG[facilityId]) {
    return FACILITY_CONFIG[facilityId];
  }
  
  return FACILITY_CONFIG['sunshine'] || {
    id: facilityId,
    name: 'Unknown Facility',
    type: FACILITY_TYPES.AGED_CARE,
    types: [FACILITY_TYPES.AGED_CARE],
    systemContext: 'agedcare',
    features: {}
  };
};

/**
 * Get facility configuration by ID (async - from service)
 * @param {string} facilityId - Facility ID
 * @returns {Promise<object>} Facility configuration
 */
export const getFacilityConfigAsync = async (facilityId) => {
  try {
    const result = await FacilityService.getById(facilityId);
    if (result.success && result.data) {
      return {
        id: result.data.id,
        name: result.data.name,
        type: result.data.primaryType,
        types: result.data.facilityTypes,
        systemContext: result.data.systemContext,
        features: result.data.features,
        agedCare: result.data.agedCare,
        businessGroup: result.data.businessGroup,
        address: result.data.address
      };
    }
  } catch (e) {
    console.warn('Error loading facility from service:', e);
  }
  
  return getFacilityConfig(facilityId);
};

/**
 * Check if facility supports a feature
 * @param {string} facilityId - Facility ID
 * @param {string} feature - Feature name
 * @returns {boolean}
 */
export const facilitySupportsFeature = (facilityId, feature) => {
  const config = getFacilityConfig(facilityId);
  return config.features[feature] || false;
};

/**
 * Check if facility is Aged Care
 * @param {string} facilityId - Facility ID
 * @returns {boolean}
 */
export const isAgedCareFacility = (facilityId) => {
  const config = getFacilityConfig(facilityId);
  return config.types.includes(FACILITY_TYPES.AGED_CARE);
};

/**
 * Check if facility is In-House / Home Care
 * @param {string} facilityId - Facility ID
 * @returns {boolean}
 */
export const isInHouseFacility = (facilityId) => {
  const config = getFacilityConfig(facilityId);
  return config.type === FACILITY_TYPES.IN_HOUSE;
};

/**
 * Get system context for facility
 * @param {string} facilityId - Facility ID
 * @returns {string} System context ('agedcare')
 */
export const getFacilitySystemContext = (facilityId) => {
  const config = getFacilityConfig(facilityId);
  return config.systemContext;
};

/**
 * Get all facilities of a specific type
 * @param {string} type - Facility type
 * @returns {array} Array of facility configs
 */
export const getFacilitiesByType = (type) => {
  return Object.values(FACILITY_CONFIG).filter(facility => 
    facility.types.includes(type)
  );
};

export default {
  FACILITY_TYPES,
  FACILITY_CONFIG,
  getFacilityConfig,
  facilitySupportsFeature,
  isAgedCareFacility,
  isInHouseFacility,
  getFacilitySystemContext,
  getFacilitiesByType
};
